import * as React from "react";
import classNames from "classnames";
import styled from "@emotion/styled";
import { isBlank } from "@/utils/is";
import { useTheme } from "@/hooks/useTheme";
import { useFormHandler } from "@/utils/hooks";
import Article from "@/components/molecules/Article";
import FormBlock from "@/components/molecules/FormBlock";
import FormContainer from "@/components/atoms/FormContainer";
import { balconyDirectionOptions } from "@/constants/BalconyDirection";
import { floorPlanOptions } from "@/constants/FloorPlan";
import form, { defaultIfBlank, ifResponseValueBlank } from "@/utils/form";
import { validatePropertyDetail as validator } from "@/utils/validation";
import FormContext, { initFormStore, updateFormValues } from "@/contexts/Form";
import contract, { HIDDEN_COMPONENT_OPTIONS, isHiddenComponent } from "@/utils/contract";
import ContractContext, { initContractStore } from "@/contexts/Contract";
import infoIcon from "@/assets/icons/info-icon.svg";

const Span = styled.span`
  font-size: 12px;
  letter-spacing: 0px;
  color: #333333;
  font-weight: 400;
  opacity: 1;
  text-align: left;
`;

const Alignment = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled.img`
  width: 14px;
  height: 14px;
  margin-right: 5px;
`;

const Mansion = () => {
  const { COLOR_THEME, isNewTheme } = useTheme();
  const {
    state: { values, validates, units },
    dispatch,
  } = React.useContext(FormContext) || initFormStore(form);
  const {
    state: { featuresObj },
  } = React.useContext(ContractContext) || initContractStore(contract);

  const [unit, setUnit] = React.useState("");
  const handleRoomNumber = useFormHandler("roomNumber", validator.roomNumber);
  const handleStory = useFormHandler("story", validator.story);
  const handleUnitSize = useFormHandler("unitSize", validator.unitSize);
  const handleBalconyDirection = useFormHandler("balconyDirection", validator.balconyDirection);
  const handleFloorPlan = useFormHandler("floorPlan", validator.floorPlan);

  React.useEffect(() => {
    let updateValues = {
      values: {
        story: "",
        unitSize: "",
        balconyDirection: "",
        floorPlan: "",
        roomNumber: "",
        buildingName: "",
      },
      validates: {
        story: "",
        unitSize: "",
        balconyDirection: "",
        floorPlan: "",
        roomNumber: "",
      },
    };
    const unitObj = units.find(u => `${u.id}` === unit);
    if (unitObj) {
      updateValues = {
        ...updateValues,
        values: {
          story: `${ifResponseValueBlank(unitObj.story)}`,
          unitSize: `${ifResponseValueBlank(unitObj.unitSize)}`,
          balconyDirection: ifResponseValueBlank(unitObj.balconyDirection),
          floorPlan: ifResponseValueBlank(unitObj.floorPlan),
          roomNumber: ifResponseValueBlank(unitObj.roomNumber),
          buildingName: ifResponseValueBlank(unitObj.buildingName),
        },
      };
    }
    dispatch(updateFormValues(updateValues));
  }, [unit]);

  const hiddenLivingAddressComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_ADDRESS);

  /**
   * 売却物件[所在地]部屋番号変更時、
   * [お住いの住所]建物名を更新する
   */
  React.useEffect(() => {
    if (values.isSame && !hiddenLivingAddressComponent) {
      dispatch(
        updateFormValues({
          values: {
            building: `${isBlank(validates.propertyName) ? values.propertyName : ""}${
              values.buildingName ? values.buildingName : ""}${
              isBlank(validates.roomNumber) ? values.roomNumber : ""
            }`,
          },
          validates: {
            building: "",
          },
        })
      );
    }
  }, [values.roomNumber, hiddenLivingAddressComponent]);

  const unitOptions: Option[] = React.useMemo(() => {
    return units.map(mansionUnitResponse => {
      const { id, roomNumber, unitSize, story, floorPlan, balconyDirection, buildingName } = mansionUnitResponse;
      let label = `${defaultIfBlank(roomNumber, '-')}号室 (${defaultIfBlank(unitSize, '-')}㎡/${
        defaultIfBlank(story, '-')}階/${defaultIfBlank(floorPlan, '-')}/${defaultIfBlank(balconyDirection, '-')})`;
      if (buildingName) {
        label += ` (${buildingName})`;
      }

      return { label, value: `${id}` };
    });
  }, [units]);

  const formBlockProps = {
    className: "form",
    backgroundColor: "#ffffff",
  };

  const isCssOverride = classNames({
    override: isNewTheme,
  });

  return (
    <Article.Block className="border-none bgWhite">
      <FormContainer className={isCssOverride}>
        {units.length > 0 && (
          <FormBlock className="underline form" backgroundColor="#ffffff">
            <FormBlock.Label
              value="住戸"
              background={COLOR_THEME.extras?.required_icon?.background}
              text={COLOR_THEME.extras?.required_icon?.text}
              border={COLOR_THEME.extras?.required_icon?.border}
            />
            <FormBlock.Row>
              <FormBlock.Select
                description={
                  <Alignment>
                    <StyledIcon src={infoIcon} height="14" width="14" />
                    <Span>住戸を選択すると、住戸情報を自動入力できます</Span>
                  </Alignment>
                }
                options={unitOptions}
                width="400px"
                className="width100Per"
                onChange={ev => setUnit(ev.target.value)}
                value={unit}
                aria-label="マンション検索をすると、物件情報を一部自動で入力できます"
                background={COLOR_THEME.extras?.invalid_state?.background}
              />
            </FormBlock.Row>
          </FormBlock>
        )}
        <FormBlock {...formBlockProps}>
          <FormBlock.Label
            value="住戸情報"
            required
            background={COLOR_THEME.extras?.required_icon?.background}
            text={COLOR_THEME.extras?.required_icon?.text}
            border={COLOR_THEME.extras?.required_icon?.border}
          />
          <FormBlock.Row>
            <FormBlock.Input
              description="部屋番号"
              aria-label="部屋番号"
              after="号室"
              placeholder="例）10"
              onChange={ev => handleRoomNumber(ev.target.value)}
              width="138px"
              className="width100px"
              value={values.roomNumber}
              required
              validateMessage={validates.roomNumber}
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
          </FormBlock.Row>
          <FormBlock.Row>
            <FormBlock.Input
              description="所在階"
              aria-label="所在階"
              after="階"
              placeholder="例）10"
              onChange={ev => handleStory(ev.target.value)}
              width="124px"
              className="width100px"
              value={values.story}
              required
              validateMessage={validates.story}
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
          </FormBlock.Row>
          <FormBlock.Row>
            <FormBlock.Input
              description="専有面積"
              aria-label="専有面積"
              placeholder="例）50.0"
              after="㎡"
              onChange={ev => handleUnitSize(ev.target.value)}
              width="124px"
              className="width100px"
              value={values.unitSize}
              required
              validateMessage={validates.unitSize}
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
          </FormBlock.Row>
          <FormBlock.Row>
            <FormBlock.Select
              description="間取り"
              aria-label="間取り"
              options={floorPlanOptions}
              onChange={ev => handleFloorPlan(ev.target.value)}
              width="200px"
              className="width200px"
              value={values.floorPlan}
              required
              validateMessage={validates.floorPlan}
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
          </FormBlock.Row>
          <FormBlock.Row>
            <FormBlock.Select
              description="バルコニー方向"
              aria-label="バルコニー方向"
              options={balconyDirectionOptions}
              onChange={ev => handleBalconyDirection(ev.target.value)}
              width="200px"
              className="width200px"
              value={values.balconyDirection}
              required
              validateMessage={validates.balconyDirection}
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
          </FormBlock.Row>
        </FormBlock>
      </FormContainer>
    </Article.Block>
  );
};

export default Mansion;
